import React from 'react';
import clsx from 'clsx';
import {ERROR, FALLBACK} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useDrivingInfoEu, {formatNumberWithLimitAndCommas} from '@di-shared/features/history/driving-info/useDrivingInfo.eu';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';
import {calculateRelativePercent} from '@di-shared/utils/graph';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {addDate} from '@shared/dateUtils';

const DRIVING_DISTANCE_GRAPH = {
  PERCENT: 80,
};

export default function DrivingDistanceSectionEu({vin}: {vin: string}) {
  const {drivingData, isCurrentDrivingDistanceNone} = useDrivingInfoEu({vin});

  // CCS 동의 당월
  if (drivingData.isCcsAgreedThisMonth) {
    return <DrivingDistanceSectionAgree vin={vin} />;
  }

  // 당월 주행없음
  if (isCurrentDrivingDistanceNone) {
    return <DrivingDistanceSectionNone vin={vin} />;
  }

  // 당월 주행
  return <DrivingDistanceSectionDefault vin={vin} />;
}

// FALLBACK(LOADING, ERROR) UI
DrivingDistanceSectionEu.Error = () => <DrivingDistanceSectionFallback fallback={FALLBACK.ERROR} />;
DrivingDistanceSectionEu.Loading = () => <DrivingDistanceSectionFallback fallback={FALLBACK.LOADING} />;

function DrivingDistanceSectionFallback({fallback}: {fallback: FALLBACK}) {
  const {t} = useInferableTranslation();
  const today = new Date();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultDistanceSymbol} = useUnitConverter();
  return (
    <div
      className={clsx('section_container trip_month_distance', {skeleton: fallback === FALLBACK.LOADING})}
      aria-hidden={fallback === FALLBACK.LOADING && 'true'}
    >
      <div className="section_container_header">
        <h3 className="body_medium_b">{t('ctn_home_distance_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}</h3>
      </div>
      <div className="trip_month type_error">
        <div
          className="trip_month_info"
          role="text"
          aria-label={`${ERROR.DASH}${defaultDistanceSymbol}`}
        >
          <span className="trip_month_value">{ERROR.DASH}</span>
          <span className="trip_month_unit">{defaultDistanceSymbol}</span>
        </div>
        <span
          className="trip_month_compare"
          role="text"
          aria-label={`${ERROR.DASH}${defaultDistanceSymbol}`}
        >
          <span className="trip_month_value">{ERROR.DASH}</span>
          <span className="trip_month_unit">{defaultDistanceSymbol}</span>
        </span>
        <p className="trip_month_desc"></p>
        <div
          className="trip_month_graph_area"
          aria-hidden="true"
        >
          <div className="distance_graph_wrap">
            <div className="bar_graph type_horizon">
              <div className="bar_graph_item">
                <span
                  className="graph_bar"
                  style={{width: `${DRIVING_DISTANCE_GRAPH.PERCENT}%`}}
                />
              </div>
              <span className="graph_title">{CldrDatetimeFormatter.format(today, 'MMM')}</span>
              <div className="bar_graph_item">
                <span
                  className="graph_bar"
                  style={{width: `${DRIVING_DISTANCE_GRAPH.PERCENT}%`}}
                />
              </div>
              <span className="graph_title">{t('ctn_home_distance_my_average')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DrivingDistanceSectionAgree({vin}: {vin: string}) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {currentDrivingDistance} = useDrivingInfoEu({vin});
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  return (
    <div className="section_container trip_month_distance">
      <LinkTo
        to={ROUTE_PATHS.HISTORY.getFullPath(vin)}
        className="section_container_full_btn"
        aria-label={t('ctn_home_distance_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}
        onClickBypass={() =>
          emitEventLog({
            event: 'di_driveinfo_interaction',
            data_action: 'view banner',
            data_name: 'My Trips Data including Monthly Driving Distance',
          })
        }
      />
      <div className="section_container_header">
        <h3 className="body_medium_b">{t('ctn_home_distance_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}</h3>
      </div>
      <div className="trip_month">
        <div
          className="trip_month_info"
          role="text"
          aria-label={`${formatNumberWithLimitAndCommas(convertDistanceValue(currentDrivingDistance.totalDistance))}${convertDistanceSymbol(currentDrivingDistance.totalDistance)}`}
        >
          <span className="trip_month_value">
            {formatNumberWithLimitAndCommas(convertDistanceValue(currentDrivingDistance.totalDistance))}
          </span>
          <span className="trip_month_unit">{convertDistanceSymbol(currentDrivingDistance.totalDistance)}</span>
        </div>
        <p className="trip_month_desc">
          {t('msg_home_distance_enough_data_$n', {n: CldrDatetimeFormatter.format(addDate(today, {months: 1}), 'MMM')})}
        </p>
        <div
          className="trip_month_graph_area"
          aria-hidden="true"
        >
          <div className="distance_graph_wrap">
            <div className="bar_graph type_horizon">
              <div className="bar_graph_item is-active">
                <span
                  className="graph_bar"
                  style={{width: `${DRIVING_DISTANCE_GRAPH.PERCENT}%`}}
                >
                  <span className="speech_bubble">{formatNumberWithLimitAndCommas(currentDrivingDistance.totalDistance)}</span>
                </span>
              </div>
              <span className="graph_title">{CldrDatetimeFormatter.format(today, 'MMM')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DrivingDistanceSectionNone({vin}: {vin: string}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();

  return (
    <div className="section_container trip_month_distance">
      <LinkTo
        to={ROUTE_PATHS.HISTORY.getFullPath(vin)}
        className="section_container_full_btn"
        aria-label={t('ctn_home_distance_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}
        onClickBypass={() =>
          emitEventLog({
            event: 'di_driveinfo_interaction',
            data_action: 'view banner',
            data_name: 'My Trips Data including Monthly Driving Distance',
          })
        }
      />

      <div className="section_container_header">
        <h3 className="body_medium_b">{t('ctn_home_distance_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}</h3>
      </div>
      <div className="trip_month type_error">
        <p
          className="trip_month_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_home_distance_no_driving')}
        </p>
        <div
          className="trip_month_graph_area"
          aria-hidden="true"
        >
          <div className="distance_graph_wrap">
            <div className="bar_graph type_horizon">
              <div className="bar_graph_item">
                <span
                  className="graph_bar"
                  style={{width: `${DRIVING_DISTANCE_GRAPH.PERCENT}%`}}
                />
              </div>
              <span className="graph_title">{CldrDatetimeFormatter.format(today, 'MMM')}</span>
              <div className="bar_graph_item">
                <span
                  className="graph_bar"
                  style={{width: `${DRIVING_DISTANCE_GRAPH.PERCENT}%`}}
                />
              </div>
              <span className="graph_title">{t('ctn_home_distance_my_average')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DrivingDistanceSectionDefault({vin}: {vin: string}) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {drivingData, currentDrivingDistance} = useDrivingInfoEu({vin});
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();

  const averageDrivingDistanceCompare = currentDrivingDistance.totalDistance - drivingData.avgDistance;
  const drivingDistanceGraphPercentage = calculateRelativePercent({
    value1: currentDrivingDistance.totalDistance,
    value2: drivingData.avgDistance,
    percent: DRIVING_DISTANCE_GRAPH.PERCENT,
  });

  return (
    <div className="section_container trip_month_distance">
      <LinkTo
        to={ROUTE_PATHS.HISTORY.getFullPath(vin)}
        className="section_container_full_btn"
        aria-label={t('ctn_home_distance_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}
        onClickBypass={() =>
          emitEventLog({
            event: 'di_driveinfo_interaction',
            data_action: 'view banner',
            data_name: 'My Trips Data including Monthly Driving Distance',
          })
        }
      />
      <div className="section_container_header">
        <h3 className="body_medium_b">{t('ctn_home_distance_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}</h3>
      </div>
      <div className="trip_month">
        <div
          className="trip_month_info"
          role="text"
          aria-label={`${formatNumberWithLimitAndCommas(convertDistanceValue(currentDrivingDistance.totalDistance))}${convertDistanceSymbol(currentDrivingDistance.totalDistance)}`}
        >
          <span className="trip_month_value">
            {formatNumberWithLimitAndCommas(convertDistanceValue(currentDrivingDistance.totalDistance))}
          </span>
          <span className="trip_month_unit">{convertDistanceSymbol(currentDrivingDistance.totalDistance)}</span>
        </div>

        <span
          className="trip_month_compare"
          role="text"
          aria-label={`${averageDrivingDistanceCompare >= 0 ? t('ctn_mytrips_recent_rise') : t('ctn_mytrips_recent_fall')}${formatNumberWithLimitAndCommas(convertDistanceValue(Math.abs(averageDrivingDistanceCompare)))}${convertDistanceSymbol(averageDrivingDistanceCompare)}`}
        >
          <span
            className={clsx('ic', {
              ic_di_count_up: averageDrivingDistanceCompare >= 0,
              ic_di_count_down: averageDrivingDistanceCompare < 0,
            })}
          >
            <span className="blind">
              {averageDrivingDistanceCompare >= 0 ? t('ctn_mytrips_recent_rise') : t('ctn_mytrips_recent_fall')}
            </span>
          </span>
          <span className="trip_month_value">
            {formatNumberWithLimitAndCommas(convertDistanceValue(Math.abs(averageDrivingDistanceCompare)))}
          </span>
          <span className="trip_month_unit">{convertDistanceSymbol(averageDrivingDistanceCompare)}</span>
        </span>

        <p className="trip_month_desc">
          {averageDrivingDistanceCompare === 0
            ? t('msg_home_distance_same')
            : averageDrivingDistanceCompare > 0
              ? t('msg_home_distance_more')
              : t('msg_home_distance_less')}
        </p>

        <div
          className="trip_month_graph_area"
          aria-hidden="true"
        >
          <div className="distance_graph_wrap">
            <div className="bar_graph type_horizon">
              <div className="bar_graph_item is-active">
                <span
                  className="graph_bar"
                  style={{
                    width: averageDrivingDistanceCompare >= 0 ? `${DRIVING_DISTANCE_GRAPH.PERCENT}%` : `${drivingDistanceGraphPercentage}%`,
                  }}
                >
                  <span className="speech_bubble">{formatNumberWithLimitAndCommas(currentDrivingDistance.totalDistance)}</span>
                </span>
              </div>
              <span className="graph_title">{CldrDatetimeFormatter.format(today, 'MMM')}</span>

              <div className="bar_graph_item">
                <span
                  className="graph_bar"
                  style={{
                    width: averageDrivingDistanceCompare >= 0 ? `${drivingDistanceGraphPercentage}%` : `${DRIVING_DISTANCE_GRAPH.PERCENT}%`,
                  }}
                />
              </div>
              <span className="graph_title">{t('ctn_home_distance_my_average')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
